(function () {
  'use strict';

  if (window.__embeddedCheckoutModuleLoaded) return;
  window.__embeddedCheckoutModuleLoaded = true;

  let stripe = null;
  let checkoutModalOpen = false;
  // Track any global embedded checkout instance to avoid duplicates
  let globalCheckoutInstance = null; // from initEmbeddedCheckout
  let globalCheckoutElement = null; // from elements().create('embedded-checkout') if any external code uses it
  // Prevent concurrent creation/race conditions across multiple triggers
  let checkoutCreating = false;
  // Keep track of mounted checkout state to avoid re-mounting
  let checkoutMounted = false;
  let lastCheckoutOptions = null;
  let activeSubscriptionFlow = null;

  const SUBSCRIPTION_EVENTS = new Set([
    'subscription_dialog_opened',
    'subscription_dialog_blocked',
    'subscription_auth_required',
    'subscription_auth_selected',
    'begin_checkout',
    'subscription_checkout_ready',
    'subscription_checkout_failed',
    'subscription_checkout_completed',
    'subscription_dialog_closed',
  ]);

  function subscriptionPlan(value) {
    return value === 'annual' ? 'annual' : 'monthly';
  }

  function subscriptionTrigger(value) {
    const allowed = new Set(['account', 'art', 'chat', 'header', 'pricing', 'tool', 'unknown']);
    if (allowed.has(value)) return value;
    try {
      const node = document.activeElement;
      if (node && node.closest) {
        if (node.closest('.header-subscribe, header')) return 'header';
        if (node.closest('[data-tool], .tool-page, .tools-grid')) return 'tool';
        if (node.closest('.pricing, [data-plan], #pricing')) return 'pricing';
        if (node.closest('#account-page, .account-page, .account-card')) return 'account';
      }
    } catch { /* Error ignored */ }
    return 'unknown';
  }

  function durationBucket(startedAt) {
    const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    if (seconds < 5) return 'under_5s';
    if (seconds < 30) return '5_to_29s';
    if (seconds < 120) return '30_to_119s';
    return '120s_plus';
  }

  function dialogInstanceId() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch { /* Error ignored */ }
    return `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function trackSubscriptionEvent(name, details = {}) {
    if (!SUBSCRIPTION_EVENTS.has(name)) return;
    try {
      if (name === 'subscription_dialog_opened' && !activeSubscriptionFlow) {
        activeSubscriptionFlow = {
          plan: subscriptionPlan(details.plan),
          trigger: subscriptionTrigger(details.trigger),
          openedAt: Date.now(),
          stage: 'dialog',
          authTransition: false,
          instanceId: dialogInstanceId(),
        };
      }
      if (activeSubscriptionFlow) {
        if (name === 'subscription_auth_required') activeSubscriptionFlow.stage = 'authentication';
        if (name === 'begin_checkout') activeSubscriptionFlow.stage = 'checkout_loading';
        if (name === 'subscription_checkout_ready') activeSubscriptionFlow.stage = 'checkout_ready';
        if (name === 'subscription_checkout_failed') activeSubscriptionFlow.stage = 'failed';
        if (name === 'subscription_checkout_completed') activeSubscriptionFlow.stage = 'completed';
      }
      const properties = {
        flow: 'subscription',
        plan: subscriptionPlan(details.plan),
        trigger: subscriptionTrigger(details.trigger),
        page_path: String(window.location.pathname || '/').slice(0, 240),
      };
      if (details.stage) properties.stage = String(details.stage).slice(0, 40);
      if (details.reason) properties.reason = String(details.reason).slice(0, 40);
      if (details.auth_method === 'login' || details.auth_method === 'sign_up') {
        properties.auth_method = details.auth_method;
      }
      if (typeof details.authenticated === 'boolean') {
        properties.authenticated = details.authenticated;
      }
      if (details.duration_bucket) properties.duration_bucket = details.duration_bucket;
      const instanceId = details.dialog_instance_id ||
        (activeSubscriptionFlow && activeSubscriptionFlow.instanceId);
      if (instanceId) properties.dialog_instance_id = String(instanceId).slice(0, 64);
      if (typeof window.th === 'function') window.th('track', name, properties);
    } catch { /* Error ignored */ }
  }

  window.trackSubscriptionEvent = trackSubscriptionEvent;

  const TOPUP_CLICK_HANDLER_KEY = '__embeddedCheckoutTopupClickHandler';
  const TOPUP_CLICK_BOUND_KEY = '__embeddedCheckoutTopupClickBound';

  function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function applyCreditBalance(data) {
    if (!data) return;
    window.userData = window.userData || {};
    if (Object.prototype.hasOwnProperty.call(data, 'free_credits')) {
      window.userData.free_credits = toNumber(data.free_credits);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'paid_credits')) {
      window.userData.paid_credits = toNumber(data.paid_credits);
    }
    const total =
      Object.prototype.hasOwnProperty.call(data, 'credits')
        ? toNumber(data.credits)
        : toNumber(window.userData.free_credits) +
          toNumber(window.userData.paid_credits);
    window.userData.credits = total;
    document.querySelectorAll('.api-credits-balance-usd').forEach((node) => {
      node.textContent = total.toFixed(2);
    });
    document.querySelectorAll('.api-credits-balance').forEach((node) => {
      node.textContent = String(total);
    });
    window.dispatchEvent(
      new CustomEvent('netwrck:credits-updated', {
        detail: {
          free_credits: toNumber(window.userData.free_credits),
          paid_credits: toNumber(window.userData.paid_credits),
          credits: total,
        },
      })
    );
  }

  function toggleAccountAutoTopup() {
    const settings = document.getElementById('autotopup-settings');
    if (!settings) return false;

    const checkbox = document.getElementById('autotopup-enabled');
    const computed = window.getComputedStyle(settings);
    const hidden =
      computed.display === 'none' ||
      settings.hidden === true ||
      settings.classList.contains('is-hidden');
    const showSettings = hidden;

    if (checkbox) {
      checkbox.checked = showSettings;
      const mdlWrapper = checkbox.parentElement;
      if (mdlWrapper && mdlWrapper.MaterialCheckbox) {
        if (showSettings) {
          mdlWrapper.MaterialCheckbox.check();
        } else {
          mdlWrapper.MaterialCheckbox.uncheck();
        }
      }
    }

    const useJquery = typeof window.$ === 'function' && window.$.fn;
    if (useJquery) {
      const $settings = window.$(settings);
      if (showSettings) {
        $settings.stop(true, true).slideDown(200);
      } else {
        $settings.stop(true, true).slideUp(200);
      }
    } else {
      settings.style.display = showSettings ? 'block' : 'none';
      if (!showSettings) {
        settings.classList.add('is-hidden');
      } else {
        settings.classList.remove('is-hidden');
      }
    }

    if (checkbox) {
      try {
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      } catch { /* Error ignored */ }
    }

    if (showSettings) {
      requestAnimationFrame(() => {
        try {
          settings.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* Error ignored */ }
        const focusTarget = settings.querySelector(
          'input, select, textarea, button'
        );
        if (focusTarget && typeof focusTarget.focus === 'function') {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch { /* Error ignored */ }
        }
      });
    }

    return true;
  }

  function ensureTopupClickHandler() {
    if (!window[TOPUP_CLICK_HANDLER_KEY]) {
      window[TOPUP_CLICK_HANDLER_KEY] = function handleTopupClick(e) {
        const topupLink =
          e.target.id === 'topup-credits-link'
            ? e.target
            : e.target.closest
              ? e.target.closest('#topup-credits-link')
              : null;
        if (topupLink) {
          e.preventDefault();
          e.stopPropagation();
          if (toggleAccountAutoTopup()) {
            return;
          }
          const dialog =
            document.getElementById('topup-credits-dialog') ||
            document.getElementById('credits-checkout-dialog');
          if (dialog && typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            openCheckoutModal({ amount: 25, type: 'credits' });
          }
          return;
        }

        if (
          e.target.tagName === 'A' ||
          (e.target.closest && e.target.closest('a'))
        ) {
          return;
        }

        if (
          (e.target.matches &&
            e.target.matches(
              '.topup-btn, .add-credits-btn, .buy-credits-btn, #add-credits-btn'
            )) ||
          (e.target.closest &&
            e.target.closest(
              '.topup-btn, .add-credits-btn, .buy-credits-btn, #add-credits-btn'
            ))
        ) {
          e.preventDefault();
          e.stopPropagation();

          const btn =
            (e.target.closest &&
              e.target.closest(
                '.topup-btn, .add-credits-btn, .buy-credits-btn, #add-credits-btn'
              )) ||
            e.target;
          if (btn && btn.id === 'add-credits-btn') {
            const dialog = document.getElementById('topup-credits-dialog');
            if (dialog && typeof dialog.showModal === 'function') {
              if (!dialog.open) dialog.showModal();
              return;
            }
          }
          const amount = (btn && btn.dataset && btn.dataset.amount) || 25;
          openCheckoutModal({ amount, type: 'credits' });
          return;
        }

        const payNow =
          e.target.id === 'topup-pay-now'
            ? e.target
            : e.target.closest
              ? e.target.closest('#topup-pay-now')
              : null;
        if (payNow) {
          e.preventDefault();
          e.stopPropagation();
          const dialog = payNow.closest('dialog') || document.getElementById('topup-dialog');
          let amount = 150;
          const selected = dialog && dialog.querySelector('.amount-btn.selected');
          if (selected && selected.dataset.amount) amount = parseInt(selected.dataset.amount, 10) || amount;
          const custom = dialog && dialog.querySelector('#topup-custom-amount');
          if (custom && custom.value && Number(custom.value) > 0) amount = parseInt(custom.value, 10) || amount;
          if (dialog && dialog.open && typeof dialog.close === 'function') {
            try { dialog.close(); } catch { /* Error ignored */ }
          }
          openCheckoutModal({ amount, type: 'credits' });
          return;
        }

        if (
          (e.target.matches &&
            e.target.matches('.subscribe-btn, .subscription-btn')) ||
          (e.target.closest &&
            e.target.closest('.subscribe-btn, .subscription-btn'))
        ) {
          e.preventDefault();
          e.stopPropagation();

          const btn =
            (e.target.closest &&
              e.target.closest('.subscribe-btn, .subscription-btn')) ||
            e.target;
          const subType = (btn && btn.dataset && btn.dataset.type) || 'monthly';
          openCheckoutModal({ amount: subType, type: 'subscription' });
        }
      };
    }
    return window[TOPUP_CLICK_HANDLER_KEY];
  }

  function bindTopupClickHandler(force = false) {
    const alreadyBound = Boolean(window[TOPUP_CLICK_BOUND_KEY]);
    if (alreadyBound && !force) {
      return;
    }

    if (alreadyBound && force && window[TOPUP_CLICK_HANDLER_KEY]) {
      try {
        document.removeEventListener(
          'click',
          window[TOPUP_CLICK_HANDLER_KEY]
        );
      } catch { /* Error ignored */ }
      window[TOPUP_CLICK_BOUND_KEY] = false;
    }

    if (!window[TOPUP_CLICK_BOUND_KEY]) {
      const handler = ensureTopupClickHandler();
      document.addEventListener('click', handler);
      window[TOPUP_CLICK_BOUND_KEY] = true;
    }
  }

  function handlePurchaseButtonClick(e) {
    e.preventDefault();

    const selectedRadio = document.querySelector(
      'input[name="credit-package"]:checked'
    );
    let amount = 25;

    if (selectedRadio) {
      if (selectedRadio.value === 'custom') {
        const customInput = document.getElementById('custom-credits-amount');
        amount = parseInt(customInput.value) || 25;
      } else {
        amount = parseInt(selectedRadio.value) || 25;
      }
    }

    const topupDialog = document.getElementById('topup-credits-dialog');
    if (topupDialog && typeof topupDialog.close === 'function') {
      topupDialog.close();
    }

    // Destroy checkout when switching to credits (different type)
    closeCheckoutModal(true);
    openCheckoutModal({ amount, type: 'credits' });
  }

  function isAnyCheckoutDialogOpen() {
    try {
      const subscribeDlg = document.getElementById('subscribe-dialog');
      const stripeDlg = document.getElementById('stripe-checkout-modal');
      return !!(
        (subscribeDlg && subscribeDlg.open === true) ||
        (stripeDlg && stripeDlg.open === true)
      );
    } catch (_) {
      return false;
    }
  }

  function bindDialogLifecycleHandlers(modal) {
    if (!modal || modal.__checkoutLifecycleBound) return;
    try {
      // Add close button handler for subscribe dialog
      if (modal.id === 'subscribe-dialog') {
        const closeBtn = modal.querySelector('.close');
        if (closeBtn && !closeBtn.__boundCloseHandler) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            closeCheckoutModal();
          });
          closeBtn.__boundCloseHandler = true;
        }
        modal.addEventListener('click', (e) => {
          const action = e.target && e.target.closest
            ? e.target.closest('[data-subscription-auth]')
            : null;
          if (!action) return;
          trackSubscriptionEvent('subscription_auth_selected', {
            plan: activeSubscriptionFlow && activeSubscriptionFlow.plan,
            trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
            auth_method: action.dataset.subscriptionAuth,
          });
          if (activeSubscriptionFlow) activeSubscriptionFlow.authTransition = true;
        });
      }
      
      modal.addEventListener('close', () => {
        if (modal.__checkoutClosing) {
          modal.__checkoutClosing = false;
          return;
        }
        try { closeCheckoutModal(); } catch { /* Error ignored */ }
      });
      modal.addEventListener('cancel', (e) => {
        // Prevent default cancel glitches and ensure cleanup
        try { e.preventDefault(); } catch { /* Error ignored */ }
        try { closeCheckoutModal(); } catch { /* Error ignored */ }
      });
      modal.__checkoutLifecycleBound = true;
    } catch { /* Error ignored */ }
  }

  function destroyAllEmbeddedCheckouts() {
    try {
      // Destroy instance tracked on subscribe or checkout modals
      const subscribeDialog = document.getElementById('subscribe-dialog');
      const checkoutDialog = document.getElementById('stripe-checkout-modal');
      const topupDialog = document.getElementById('topup-credits-dialog');
      const creditsDialog = document.getElementById('credits-checkout-dialog');
      const addCreditsDialog = document.getElementById('topup-dialog');
      [subscribeDialog, checkoutDialog, topupDialog, creditsDialog, addCreditsDialog].forEach((dlg) => {
        if (
          dlg &&
          dlg.checkoutInstance &&
          typeof dlg.checkoutInstance.destroy === 'function'
        ) {
          try {
            dlg.checkoutInstance.destroy();
          } catch { /* Error ignored */ }
          dlg.checkoutInstance = null;
        }
      });

      // Destroy any globally tracked initEmbeddedCheckout instance
      if (
        globalCheckoutInstance &&
        typeof globalCheckoutInstance.destroy === 'function'
      ) {
        try {
          globalCheckoutInstance.destroy();
        } catch { /* Error ignored */ }
        globalCheckoutInstance = null;
      }

      // Unmount any globally tracked elements-based embedded checkout
      if (
        globalCheckoutElement &&
        typeof globalCheckoutElement.unmount === 'function'
      ) {
        try {
          globalCheckoutElement.unmount();
        } catch { /* Error ignored */ }
        globalCheckoutElement = null;
      }
    } catch (e) {
      // Swallow cleanup errors to avoid blocking user flow
      console.warn('[CHECKOUT] Cleanup warning:', e);
    }
  }

  async function initStripe() {
    if (stripe) return stripe;

    if (window.__TEST_MODE__) {
      stripe = {
        initEmbeddedCheckout: async () => ({
          mount(selector) {
            const el = document.querySelector(selector);
            if (el) {
              el.dataset.testCheckoutMounted = 'true';
              el.innerHTML = '<div class="embedded-checkout-test-frame">Test checkout</div>';
            }
          },
          destroy() {},
        }),
      };
      return stripe;
    }

    let attempts = 0;
    while (typeof Stripe === 'undefined' && attempts < 30) {
      await new Promise((resolve) => setTimeout(resolve, 250));
      attempts++;
    }

    if (typeof Stripe === 'undefined') {
      throw new Error('Stripe.js did not load');
    }

    const stripeKey =
      window.STRIPE_PUBLISHABLE_KEY ||
      (window.fixtures && window.fixtures.stripe_publishable_key) ||
      '';

    if (!stripeKey) {
      throw new Error('Stripe publishable key is not configured');
    }

    stripe = Stripe(stripeKey);
    return stripe;
  }

  function getOrCreateCheckoutModal() {
    let modal = document.getElementById('stripe-checkout-modal');
    if (!modal) {
      modal = document.createElement('dialog');
      modal.id = 'stripe-checkout-modal';
      modal.className = 'mdl-dialog';
      modal.style.cssText = `
                width: 90%;
                max-width: 600px;
                border: none;
                border-radius: 8px;
                padding: 0;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            `;

      modal.innerHTML = `
                <div class="checkout-modal-header" style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid #eee;
                    background: #f5f5f5;
                    border-radius: 8px 8px 0 0;
                ">
                    <h3 style="margin: 0; color: #333;">Complete Payment</h3>
                    <button class="close-checkout" style="
                        background: none;
                        border: none;
                        font-size: 24px;
                        cursor: pointer;
                        color: #666;
                    " aria-label="Close">&times;</button>
                </div>
                <div class="checkout-modal-body" style="padding: 20px;">
                    <div id="credits-checkout-element" style="min-height: 400px;"></div>
                    <div id="credits-checkout-loading" style="
                        text-align: center;
                        padding: 40px;
                        color: #666;
                    ">
                        <div class="mdl-spinner mdl-js-spinner is-active"></div>
                        <p>Loading payment form...</p>
                    </div>
                    <div id="credits-checkout-error" style="
                        display: none;
                        padding: 20px;
                        background: #ffebee;
                        border-radius: 4px;
                        color: #c62828;
                        margin-top: 20px;
                    "></div>
                </div>
            `;

      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeCheckoutModal();
        }
      });

      bindDialogLifecycleHandlers(modal);
    }
    // Wire close button whether modal was created or already existed
    const closeBtn = modal.querySelector('.close-checkout');
    if (closeBtn && !closeBtn.__closeBound) {
      closeBtn.addEventListener('click', () => closeCheckoutModal());
      closeBtn.__closeBound = true;
    }
    // Ensure lifecycle handlers are bound even if modal existed
    bindDialogLifecycleHandlers(modal);
    return modal;
  }

  async function openCheckoutModal(options = {}) {
    // Fast guard against concurrent creations (race across multiple triggers)
    if (checkoutCreating) {
      const started = Date.now();
      while (checkoutCreating && Date.now() - started < 5000) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (checkoutCreating) {
        console.warn('[CHECKOUT] Creation already in progress; skipping');
        if (options.type === 'subscription') {
          trackSubscriptionEvent('subscription_dialog_blocked', {
            plan: options.amount,
            trigger: options.trigger,
            reason: 'opening',
          });
        }
        return;
      }
    }
    
    // If modal is already open with same options, just show it again
    if (checkoutModalOpen && lastCheckoutOptions && 
        JSON.stringify(options) === JSON.stringify(lastCheckoutOptions)) {
      const modal = document.getElementById('subscribe-dialog') || 
                   document.getElementById('stripe-checkout-modal');
      if (modal && typeof modal.showModal === 'function') {
        // For subscribe dialog, ensure stripe section is visible when reopening
        if (modal.id === 'subscribe-dialog') {
          const loginSection = modal.querySelector('.header-login-signup');
          const stripeSection = modal.querySelector('#stripe-checkout-dialog-content');
          if (loginSection) loginSection.style.display = 'none';
          if (stripeSection) stripeSection.style.display = 'block';
        }
        modal.showModal();
      }
      if (options.type === 'subscription') {
        trackSubscriptionEvent('subscription_dialog_blocked', {
          plan: options.amount,
          trigger: options.trigger,
          reason: 'already_open',
        });
      }
      return;
    }
    
    // If different options or not open, continue with setup
    if (checkoutModalOpen) {
      // Close existing and continue
      closeCheckoutModal();
    }

    const {
      amount = 25,
      type = 'credits',
      successUrl = window.location.href,
      cancelUrl = window.location.href,
    } = options;

    // Choose container per flow: subscription uses subscribe dialog; credits uses checkout modal
    let modal = null;
    if (type === 'subscription') {
      modal = document.getElementById('subscribe-dialog');
    } else {
      modal = document.getElementById('stripe-checkout-modal');
    }
    let loadingEl, checkoutEl, errorEl;

    if (modal) {
      // Hide login section and show stripe section
      if (type === 'subscription') {
        const loginSection = modal.querySelector('.header-login-signup');
        const stripeSection = modal.querySelector(
          '#stripe-checkout-dialog-content'
        );
        if (loginSection) loginSection.style.display = 'none';
        if (stripeSection) stripeSection.style.display = 'block';

        // Check if checkout elements already exist before creating new ones
        loadingEl = stripeSection.querySelector('#subscribe-checkout-loading');
        checkoutEl = stripeSection.querySelector('#subscribe-checkout-element');
        errorEl = stripeSection.querySelector('#subscribe-checkout-error');
        
        // Only create elements if they don't exist
        if (!loadingEl || !checkoutEl || !errorEl) {
          stripeSection.innerHTML = `
                    <div id="subscribe-checkout-loading" style="text-align: center; padding: 40px; color: #666;">
                        <div class="mdl-spinner mdl-js-spinner is-active"></div>
                        <p>Loading payment form...</p>
                    </div>
                    <div id="subscribe-checkout-element" style="min-height: 400px; display: none;"></div>
                    <div id="subscribe-checkout-error" style="display: none; padding: 20px; background: #ffebee; border-radius: 4px; color: #c62828; margin-top: 20px;"></div>
                `;
          
          loadingEl = stripeSection.querySelector('#subscribe-checkout-loading');
          checkoutEl = stripeSection.querySelector('#subscribe-checkout-element');
          errorEl = stripeSection.querySelector('#subscribe-checkout-error');
        }
      } else {
        // Credits flow: use dedicated checkout modal container
        const body = modal.querySelector('.checkout-modal-body') || modal;
        body.innerHTML = `
                  <div id="credits-checkout-loading" style="text-align: center; padding: 40px; color: #666;">
                      <div class="mdl-spinner mdl-js-spinner is-active"></div>
                      <p>Loading payment form...</p>
                  </div>
                  <div id="credits-checkout-element" style="min-height: 400px; display: none;"></div>
                  <div id="credits-checkout-error" style="display: none; padding: 20px; background: #ffebee; border-radius: 4px; color: #c62828; margin-top: 20px;"></div>
              `;
        loadingEl = body.querySelector('#credits-checkout-loading');
        checkoutEl = body.querySelector('#credits-checkout-element');
        errorEl = body.querySelector('#credits-checkout-error');
      }
    } else {
      modal = getOrCreateCheckoutModal();
      loadingEl = modal.querySelector('#credits-checkout-loading');
      checkoutEl = modal.querySelector('#credits-checkout-element');
      errorEl = modal.querySelector('#credits-checkout-error');
    }

    loadingEl.style.display = 'block';
    checkoutEl.style.display = 'none';
    errorEl.style.display = 'none';
    checkoutEl.innerHTML = '';

    if (modal.style) {
      modal.style.display = '';
    }
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.style.display = 'block';
    }
    checkoutModalOpen = true;
    bindDialogLifecycleHandlers(modal);

    if (type === 'subscription') {
      activeSubscriptionFlow = {
        plan: subscriptionPlan(amount),
        trigger: subscriptionTrigger(options.trigger),
        openedAt: Date.now(),
        stage: 'dialog',
        authTransition: false,
        instanceId: dialogInstanceId(),
      };
      trackSubscriptionEvent('subscription_dialog_opened', {
        plan: activeSubscriptionFlow.plan,
        trigger: activeSubscriptionFlow.trigger,
        authenticated: Boolean(window.userData && window.userData.id),
      });
    }

    checkoutCreating = true;
    lastCheckoutOptions = { ...options };
    
    try {
      await initStripe();

      // Only destroy if we're switching checkout types or if checkout isn't mounted
      const needsNewCheckout = !globalCheckoutInstance || !checkoutMounted ||
                              (lastCheckoutOptions && JSON.stringify(options) !== JSON.stringify(lastCheckoutOptions));
      
      if (needsNewCheckout) {
        destroyAllEmbeddedCheckouts();
        checkoutMounted = false;
      }
      // Check for authentication from multiple sources
      let userData = {};


      // First, check window.userData (populated by server)
      if (window.userData && window.userData.id) {
        userData = window.userData;

        // For server-side sessions, secret might be empty - that's OK
        if (!userData.secret) {
          userData.secret = ''; // placeholder; try to hydrate from other sources next
        }
      }
      // Fallback to localStorage
      else {
        const stored = localStorage.getItem('netwrck_user') || localStorage.getItem('userData');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            // Handle nested structures - sometimes the full login response is stored
            const ud = parsed.user_data || parsed.user || parsed.data || parsed;
            const userId = ud.id || ud.ID || ud.uid || ud.user_id || '';
            if (ud && userId) {
              userData = {
                id: userId,
                email: ud.email || ud.Email || '',
                secret: ud.secret || ud.Secret || ud.api_key || '',
                is_subscribed: ud.is_subscribed || ud.IsSubscribed || false,
              };
            }
          } catch (e) {
            console.warn('[CHECKOUT] Failed to parse localStorage:', e);
          }
        }
      }

      // Try to hydrate missing secret from additional sources (cookies/API client/session-manager)
      if (!userData.secret) {
        try {
          // 1) APIClient helper if present
          if (window.getAuthCredentials) {
            const creds = window.getAuthCredentials();
            if (creds && creds.secret) {
              userData.secret = creds.secret;
            }
          }
          // 2) Cookies
          if (!userData.secret && document && document.cookie) {
            const cookies = document.cookie.split(';').reduce((acc, c) => {
              const [k, v] = c.trim().split('=');
              acc[k] = v;
              return acc;
            }, {});
            if (cookies.user_secret) {
              userData.secret = cookies.user_secret;
            }
          }
          // 3) session-manager localStorage key
          if (!userData.secret) {
            const lsSecret = localStorage.getItem('userSecret');
            if (lsSecret) {
              userData.secret = lsSecret;
            }
          }
        } catch (e) {
          console.warn(
            '[CHECKOUT] Failed to hydrate secret from fallbacks:',
            e
          );
        }
      }

      if (!userData.id) {

        if (type === 'subscription') {
          if (activeSubscriptionFlow) activeSubscriptionFlow.stage = 'authentication';
          if (activeSubscriptionFlow) activeSubscriptionFlow.authTransition = true;
          trackSubscriptionEvent('subscription_auth_required', {
            plan: amount,
            trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
            authenticated: false,
          });
        }

        // Store pending action so it can be executed after signup/login
        try {
          sessionStorage.setItem('pendingAuthAction', JSON.stringify({
            type: 'openCheckout',
            options: { amount, type, successUrl, cancelUrl }
          }));
        } catch (e) { console.warn('[CHECKOUT] Failed to store pending action:', e); }

        // Close any open modals first
        if (modal && modal.close) {
          try { modal.close(); } catch (e) { /* ignore */ }
        }

        // Show signup dialog
        if (typeof window.showSignupDialog === 'function') {
          window.showSignupDialog(null, window.location.pathname);
        } else if (typeof window.showLoginDialog === 'function') {
          window.showLoginDialog(null);
        } else {
          console.error('[CHECKOUT] No auth dialog available');
          alert('Please sign up or log in to continue');
        }

        checkoutCreating = false;
        return;
      }

      const endpoint =
        type === 'subscription'
          ? '/create-checkout-session-embedded'
          : '/api/create-credits-checkout';

      const requestBody =
        type === 'subscription'
          ? {
              uid: userData.id,
              secret: userData.secret,
              email: userData.email,
              subscription_type: amount === 'annual' ? 'annual' : 'monthly',
              referral:
                window.CookieUtils && typeof window.CookieUtils.getCookie === 'function'
                  ? window.CookieUtils.getCookie('evangeler_ref')
                  : '',
            }
          : {
              uid: userData.id,
              secret: userData.secret,
              amount: parseInt(amount),
              embedded: true,
              success_url: successUrl,
              cancel_url: cancelUrl,
            };

      if (type === 'subscription') {
        if (activeSubscriptionFlow) activeSubscriptionFlow.stage = 'checkout_loading';
        trackSubscriptionEvent('begin_checkout', {
          plan: amount,
          trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
          authenticated: true,
        });
      } else if (window.thFunnel) {
        window.thFunnel.beginCheckout('credits', { flow: 'credits', value: parseFloat(amount) || 0, currency: 'USD' });
      }

      const headers = { 'Content-Type': 'application/json' };
      if (userData && userData.secret) {
        headers['Authorization'] = `Bearer ${userData.secret}`;
        headers['X-API-Key'] = userData.secret;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to create checkout session');
      }

      const data = await response.json();

      if (data.debug && type === 'credits') {
        applyCreditBalance(data);
        loadingEl.style.display = 'none';
        checkoutEl.style.display = 'none';
        if (!window.__TEST_MODE__) {
          try {
            closeCheckoutModal(true);
          } catch { /* Error ignored */ }
        }
        if (
          window.NetwrckTools &&
          typeof window.NetwrckTools.showNotification === 'function'
        ) {
          window.NetwrckTools.showNotification('credits added', 'success');
        }
        return;
      }

      if (data.client_secret || data.clientSecret) {
        const clientSecret = data.client_secret || data.clientSecret;

        loadingEl.style.display = 'none';
        checkoutEl.style.display = 'block';

        // Only create new checkout if we don't have one or it's different
        if (!globalCheckoutInstance || !checkoutMounted) {
          const checkout = await stripe.initEmbeddedCheckout({
            clientSecret: clientSecret,
            onComplete: function handleAffiliatePurchaseComplete() {
              if (type === 'subscription') {
                trackSubscriptionEvent('subscription_checkout_completed', {
                  plan: amount,
                  trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
                  authenticated: true,
                });
              }
              try {
                if (window.thFunnel) {
                  const prices = window.evangelPurchaseAmounts || {};
                  if (type === 'subscription') {
                    const plan = subscriptionPlan(amount);
                    window.thFunnel.purchase(plan, plan === 'annual' ? prices.annual || 90 : prices.monthly || 9, { flow: 'subscription' });
                  } else {
                    window.thFunnel.purchase('credits', parseFloat(amount) || 0, { flow: 'credits' });
                  }
                }
              } catch { /* Error ignored */ }
              if (
                window.evangelFirstPurchaseOnly === true &&
                typeof window.evangelTrackConversion === 'function'
              ) {
                var configuredAmounts = window.evangelPurchaseAmounts || {};
                var purchaseAmount =
                  type === 'credits'
                    ? parseFloat(amount) || 0
                    : amount === 'annual'
                      ? configuredAmounts.annual || 90
                      : configuredAmounts.monthly || 9;
                var purchaseID =
                  'first_purchase_' +
                  window.location.hostname +
                  '_' +
                  (userData.id || userData.email || '');
                window.evangelTrackConversion(
                  purchaseAmount,
                  purchaseID,
                  userData.email
                );
              }
            },
          });

          if (type === 'subscription') {
            checkout.mount('#subscribe-checkout-element');
          } else {
            checkout.mount('#credits-checkout-element');
          }

          modal.checkoutInstance = checkout;
          globalCheckoutInstance = checkout;
          checkoutMounted = true;
          if (type === 'subscription') {
            if (activeSubscriptionFlow) activeSubscriptionFlow.stage = 'checkout_ready';
            trackSubscriptionEvent('subscription_checkout_ready', {
              plan: amount,
              trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
              authenticated: true,
            });
          }
        } else {
          // Just make sure elements are visible
          loadingEl.style.display = 'none';
          checkoutEl.style.display = 'block';
        }
      } else if (data.url) {
        try { closeCheckoutModal(); } catch { /* Error ignored */ }
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (type === 'subscription') {
        trackSubscriptionEvent('subscription_checkout_failed', {
          plan: amount,
          trigger: activeSubscriptionFlow && activeSubscriptionFlow.trigger,
          stage: activeSubscriptionFlow && activeSubscriptionFlow.stage,
          authenticated: Boolean(window.userData && window.userData.id),
        });
        if (activeSubscriptionFlow) activeSubscriptionFlow.stage = 'failed';
      }
      if (loadingEl) loadingEl.style.display = 'none';
      if (errorEl) {
        errorEl.style.display = 'block';
        errorEl.textContent =
          error.message || 'An error occurred. Please try again.';
      }
    } finally {
      // Clear the creating flag even if an error occurs
      checkoutCreating = false;
    }
  }

  function closeCheckoutModal(destroyCheckout = false) {
    // Check for subscribe dialog first (preferred), then fallback to stripe checkout modal
    let modal =
      document.getElementById('subscribe-dialog') ||
      document.getElementById('stripe-checkout-modal');
    if (modal) {
      if (activeSubscriptionFlow) {
        const flow = activeSubscriptionFlow;
        activeSubscriptionFlow = null;
        trackSubscriptionEvent('subscription_dialog_closed', {
          plan: flow.plan,
          trigger: flow.trigger,
          stage: flow.authTransition ? 'authentication' : flow.stage,
          reason: flow.authTransition ? 'auth_transition' : 'dismissed',
          duration_bucket: durationBucket(flow.openedAt),
          dialog_instance_id: flow.instanceId,
        });
      }
      // Only destroy checkout on explicit request (e.g., when switching to credits)
      if (destroyCheckout) {
        if (modal.checkoutInstance) {
          try {
            modal.checkoutInstance.destroy();
          } catch { /* Error ignored */ }
          modal.checkoutInstance = null;
        }
        
        // Clear global references as well
        if (
          globalCheckoutInstance &&
          typeof globalCheckoutInstance.destroy === 'function'
        ) {
          try {
            globalCheckoutInstance.destroy();
          } catch { /* Error ignored */ }
        }
        globalCheckoutInstance = null;
        if (
          globalCheckoutElement &&
          typeof globalCheckoutElement.unmount === 'function'
        ) {
          try {
            globalCheckoutElement.unmount();
          } catch { /* Error ignored */ }
        }
        globalCheckoutElement = null;
        checkoutMounted = false;
        lastCheckoutOptions = null;
      }

      // If using subscribe dialog, don't hide sections when closing
      // This was causing the freeze - sections stayed hidden when reopening
      // Let the next open operation decide what to show
      if (modal.id === 'subscribe-dialog') {
        const loginSection = modal.querySelector('.header-login-signup');
        const stripeSection = modal.querySelector(
          '#stripe-checkout-dialog-content'
        );
        // Remove any inline display styles to let CSS take over
        if (loginSection) loginSection.style.display = '';
        if (stripeSection) stripeSection.style.display = '';
      }

      // Close if needed
      try {
        if (typeof modal.close === 'function' && modal.open) {
          modal.__checkoutClosing = true;
          modal.close();
        } else {
          modal.style.display = 'none';
        }
      } catch { /* Error ignored */ }

      const companionModal =
        modal.id === 'subscribe-dialog'
          ? document.getElementById('stripe-checkout-modal')
          : document.getElementById('subscribe-dialog');
      try {
        if (companionModal && typeof companionModal.close === 'function' && companionModal.open) {
          companionModal.__checkoutClosing = true;
          companionModal.close();
        }
      } catch { /* Error ignored */ }

      checkoutModalOpen = false;
      checkoutCreating = false;
      checkoutMounted = false;
    }
  }

  function initTopupButtons() {
    bindTopupClickHandler();

    const purchaseBtn =
      document.getElementById('purchase-credits-btn') ||
      document.getElementById('checkout-credits-btn');
    if (purchaseBtn) {
      if (!purchaseBtn.__embeddedCheckoutBound) {
        purchaseBtn.addEventListener('click', handlePurchaseButtonClick);
        purchaseBtn.__embeddedCheckoutBound = true;
      }
    }
  }

  window.EmbeddedCheckout = {
    open: openCheckoutModal,
    close: closeCheckoutModal,
    init: initTopupButtons,
    getStripe: initStripe,
    // Allow other modules to proactively clean up before creating new instances
    destroyAll: destroyAllEmbeddedCheckouts,
    // Optional: expose globals for coordinated cleanup from other modules
    _setExternalElement(el) {
      // Record elements().create('embedded-checkout') users so we can unmount later
      globalCheckoutElement = el;
    },
  };

  window.showTopupCreditsModal = function (options = {}) {
    const { amount = 25 } = options;
    const dialog =
      document.getElementById('topup-credits-dialog') ||
      document.getElementById('credits-checkout-dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return;
      } catch { /* Error ignored */ }
    }
    openCheckoutModal({ amount, type: 'credits' });
  };

  window.handleInsufficientCredits = function (detail) {
    const required = Number(detail && (detail.required || detail.credits)) || 0;
    if (typeof window.showTopupDialog === 'function') {
      try {
        window.showTopupDialog();
        return true;
      } catch { /* Error ignored */ }
    }
    const dialog = document.getElementById('topup-dialog');
    if (dialog && typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return true;
      } catch { /* Error ignored */ }
    }
    try {
      openCheckoutModal({
        amount: 20,
        type: 'credits',
      });
      return true;
    } catch { /* Error ignored */ }
    return false;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initTopupButtons();
      // Bind lifecycle handlers if dialogs are present
      bindDialogLifecycleHandlers(document.getElementById('subscribe-dialog'));
      bindDialogLifecycleHandlers(document.getElementById('stripe-checkout-modal'));
    });
  } else {
    initTopupButtons();
    bindDialogLifecycleHandlers(document.getElementById('subscribe-dialog'));
    bindDialogLifecycleHandlers(document.getElementById('stripe-checkout-modal'));
  }

  if (typeof Stripe !== 'undefined') {
    initStripe().catch((e) => console.warn('[CHECKOUT] Failed to initialize Stripe:', e.message));
  } else {
    const checkStripe = setInterval(() => {
      if (typeof Stripe !== 'undefined') {
        clearInterval(checkStripe);
        initStripe().catch((e) => console.warn('[CHECKOUT] Failed to initialize Stripe:', e.message));
      }
    }, 100);
  }
})();
